import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import MainCard from "components/MainCard";
import SimpleBar from "components/third-party/SimpleBar";
import { IInstituteAndYearWiseMember } from "../../api/types";
import LoadingButton from "@mui/lab/LoadingButton";
import { AiFillFileExcel } from "react-icons/ai";

type Props = {
  data?: IInstituteAndYearWiseMember[];
  exportInstitute?: () => void;
  loading?: boolean;
};

export default function InstituteWiseCount({
  data,
  exportInstitute,
  loading,
}: Props) {
  if (!data || data.length === 0) {
    return (
      <MainCard darkTitle title="Institute Wise Passout Members">
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "200px" }}
        >
          <Typography variant="h6" color="textSecondary">
            No data found.
          </Typography>
        </Grid>
      </MainCard>
    );
  }

  const { years, rows, yearlyTotals, grandTotal } = pivotInstituteData(data);

  return (
    <MainCard
      darkTitle
      title="Institute Wise Passout Members"
      content={false}
      secondary={
        <LoadingButton
          size="small"
          variant="contained"
          loading={loading}
          loadingPosition="start"
          color="secondary"
          startIcon={<AiFillFileExcel size={18} />}
          onClick={exportInstitute}
        >
          Export
        </LoadingButton>
      }
    >
      <TableContainer sx={{ maxHeight: 490 }}>
        <SimpleBar sx={{ width: "100%", height: 440, overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead
              sx={{
                position: "sticky",
                top: -1,
                zIndex: 2,
              }}
            >
              <TableRow>
                <TableCell>Institute</TableCell>
                {years.map((year) => (
                  <TableCell key={year} align="center">
                    {year}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: -1,
                    zIndex: 5,
                  }}
                >
                  <strong>Total</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.instituteName}</TableCell>
                  {row.counts.map((count, i) => (
                    <TableCell key={i} align="center">
                      {count > 0 ? count : "-"}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <strong>{row.total}</strong>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "white",
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                {years.map((_, i) => (
                  <TableCell key={i} align="center">
                    <strong>
                      {yearlyTotals[i] > 0 ? yearlyTotals[i] : "-"}
                    </strong>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <strong>{grandTotal}</strong>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </SimpleBar>
      </TableContainer>
    </MainCard>
  );
}

function pivotInstituteData(data: IInstituteAndYearWiseMember[]) {
  const yearSet = new Set<number>();
  const instituteMap = new Map<string, { [year: number]: number }>();

  data.forEach(({ instituteName, year, memberCount }) => {
    yearSet.add(year);
    if (!instituteMap.has(instituteName)) {
      instituteMap.set(instituteName, {});
    }
    const yearMap = instituteMap.get(instituteName)!;
    yearMap[year] = (yearMap[year] || 0) + memberCount;
  });

  const years = Array.from(yearSet).sort((a, b) => a - b);

  const rows = Array.from(instituteMap.entries()).map(
    ([instituteName, yearMap]) => {
      const counts = years.map((year) => yearMap[year] || 0);
      const total = counts.reduce((sum, val) => sum + val, 0);
      return { instituteName, counts, total };
    }
  );

  const yearlyTotals = years.map((_, idx) =>
    rows.reduce((sum, row) => sum + row.counts[idx], 0)
  );

  const grandTotal = yearlyTotals.reduce((sum, val) => sum + val, 0);

  return { years, rows, yearlyTotals, grandTotal };
}
